/**
 * Frontend service for the trade-book import endpoint.
 */
import api from './api.service';

export interface ParsedFill {
  rowIndex: number;
  exchSeg: 'NSE' | 'BSE' | 'NFO';
  userId: string;
  side: 'BUY' | 'SELL';
  tradingSymbol: string;
  normalizedScrip: string;
  exchangeOrderNo: string;
  tradePrice: number;
  tradeQty: number;
  orderSource?: string;
  fillHash: string;
  clientId?: string;
  clientName?: string;
  status: 'new' | 'duplicate' | 'unmapped';
  dupeReason?: string;
}

export interface AggregatedTrade {
  clientId: string;
  clientName: string;
  tradeDate: string;
  scrip: string;
  exchange: 'NSE' | 'BSE';
  tradeType: string;
  lotSize: number;
  purchaseLots: number;
  purchasePrice: number;
  saleLots: number;
  salePrice: number;
  buyValue: number;
  sellValue: number;
  fillCount: number;
  fillHashes: string[];
  status: 'create' | 'update' | 'needs-review';
  reviewReason?: string;
  reviewCategory?: 'SELL_ONLY' | 'SELL_GT_BUY' | 'LOT_SIZE_UNKNOWN' | 'QTY_NOT_DIVISIBLE';
  existingTradeId?: string;
  brokerageAmount?: number;
}

/** Per-disputed-trade decision, keyed by `${clientId}|${scrip}|${exchange}`. */
export type TradeDecision = 'short' | 'ignore';
export type Classifications = Record<string, TradeDecision>;

export interface ImportPreview {
  batchId: string;
  tradeDate: string;
  filename: string;
  totalRows: number;
  parsedFills: ParsedFill[];
  aggregatedTrades: AggregatedTrade[];
  summary: {
    newFills: number;
    dupeFills: number;
    tradesToCreate: number;
    tradesToUpdate: number;
    needsReview: number;
    unmappedUserIds: string[];
  };
  errors: string[];
}

export interface ImportCommitResult extends ImportPreview {
  committed: {
    batchId: string;
    fillsWritten: number;
    fillsSkipped: number;
    tradesCreated: number;
    tradesUpdated: number;
    tradesFailed: number;
    errors: string[];
  };
}

async function postTradeBook(
  files: File[],
  opts: { tradeDate?: string; dryRun: boolean; classifications?: Classifications },
): Promise<ImportPreview | ImportCommitResult> {
  const form = new FormData();
  for (const f of files) form.append('file', f, f.name);
  if (opts.tradeDate) form.append('tradeDate', opts.tradeDate);
  form.append('dryRun', String(opts.dryRun));
  if (opts.classifications && Object.keys(opts.classifications).length > 0) {
    form.append('classifications', JSON.stringify(opts.classifications));
  }

  const response = await api.post('/imports/trade-book', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180_000,
  });
  return response.data?.data;
}

export const importsService = {
  /** Parse + return preview without writing anything. Accepts one or more files. */
  preview: (files: File[], tradeDate?: string): Promise<ImportPreview> =>
    postTradeBook(files, { tradeDate, dryRun: true }) as Promise<ImportPreview>,

  /** Parse + write fills and aggregated trades to DynamoDB. Pass per-disputed-trade
   *  decisions so SELL_ONLY trades classified 'short' are created as sell-first. */
  commit: (files: File[], tradeDate?: string, classifications?: Classifications): Promise<ImportCommitResult> =>
    postTradeBook(files, { tradeDate, dryRun: false, classifications }) as Promise<ImportCommitResult>,
};

export default importsService;
